import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceCaseRequestsApi } from '../evidenceApi';
import type { EvidenceRequestDetail } from '../evidenceTypes';

const ID = 'evidence-request-decline';

/** The column's bound — `EvidenceRequestFieldRules.DeclineReasonMaxLength`. */
const REASON_MAX = 500;

/**
 * DECLINES a pending evidence request, with a reason the requester reads on their own register
 * (product call, 09 Oct 2026). A form that COLLECTS, so the tick is on it — no `<ConfirmDialog>`.
 *
 * It moves nothing and writes nothing on the case: the request leaves the Requests folder and stays
 * on the requester's register as Declined, with this sentence.
 */
export function EvidenceRequestDeclineModal({
  request,
  onClose,
  onDeclined,
}: {
  request: EvidenceRequestDetail;
  onClose: () => void;
  onDeclined: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const gap = reason.trim() === '' ? 'Give the reason it is declined.' : null;
  const canSave = gap === null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await evidenceCaseRequestsApi.decline(request.id, request.kind, reason.trim());
      onDeclined();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The request could not be declined.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Decline ${request.requestNumber}`}
      size="lg"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => setReason(`Sample reason — not this case's evidence (${Math.random().toString(36).slice(2, 8).toUpperCase()})`),
        disabled: saving,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip disabledReason={gap}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-600">
          Nothing is received or transferred. The request leaves this case&apos;s Requests and stays on the
          requester&apos;s own register as Declined, with the reason below.
        </p>

        <Textarea
          id={`${ID}-reason`}
          label="Reason"
          required
          description="Why the case's team will not act on this request. The requester reads it."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={REASON_MAX}
          disabled={saving}
          error={error?.fieldError('reason')}
        />
      </div>
    </Modal>
  );
}
